/**
 * What a signal *does* to a renderer, as opposed to what it sets.
 *
 * Phase 2 mapped signals onto parameters: `BEAT -> rotation` wrote a number and
 * the renderer read it. That is enough for modulation and not enough for
 * mutation, because the interesting things a player does are events with
 * consequences — a miss should tear something, not lower a slider.
 *
 * So a mapping may now name an *action* instead of a parameter. The renderer is
 * handed the action and decides what it means: `SPLASH` is ink in one renderer
 * and a particle burst in another, and neither has to agree with the other. A
 * renderer implements the handful of actions it has an answer for and ignores
 * the rest — `applyMutation` is optional, and an unknown action is a no-op, so
 * adding an action here can never break an existing renderer.
 *
 * The vocabulary is deliberately about materials and forces rather than about
 * gameplay. Nothing in this list mentions score, combo or accuracy: those are
 * signals, and what they *do* is a decision the recipe makes.
 */

export const MUTATION_ACTIONS = [
  'SPAWN',
  'REMOVE',
  'EXPAND',
  'CONTRACT',
  'ROTATE',
  'SHIFT',
  'DISTORT',
  'BREAK',
  'MERGE',
  'SPLASH',
  'BLEED',
  'FADE',
  'DENSITY',
  'OPACITY',
  'INTENSITY',
  'TURBULENCE',
  'FREEZE',
  'RELEASE',
  'RANDOMIZE',
] as const;

export type MutationAction = (typeof MUTATION_ACTIONS)[number];

export function isMutationAction(value: string): value is MutationAction {
  return (MUTATION_ACTIONS as readonly string[]).includes(value);
}

/**
 * One instruction to change.
 *
 * `sustained` separates the two shapes a mutation can have. A miss is a moment:
 * it arrives once, with force, and what it did persists because the renderer
 * chose to keep it. A combo is a condition: it is true continuously and is
 * re-applied every frame. Renderers need to tell those apart — accumulating a
 * sustained mutation at 60 Hz the way you accumulate a one-shot would saturate
 * anything it touched within a second.
 */
export interface VisualMutation {
  readonly action: MutationAction;
  /** 0..1 for one-shots; for sustained, the mapped level after scaling. */
  readonly strength: number;
  /** Which signal asked for this, so a renderer can distinguish sources. */
  readonly source: string;
  /** True when re-applied every frame rather than fired once. */
  readonly sustained: boolean;
  /** Song time the mutation belongs to. */
  readonly time: number;
  /** Whatever the originating event carried. Renderers may ignore it. */
  readonly detail: Readonly<Record<string, number | string | boolean>>;
}

export function mutation(
  action: MutationAction,
  strength: number,
  source: string,
  time: number,
  options: {
    readonly sustained?: boolean;
    readonly detail?: Readonly<Record<string, number | string | boolean>>;
  } = {},
): VisualMutation {
  return {
    action,
    strength: Number.isFinite(strength) ? strength : 0,
    source,
    sustained: options.sustained ?? false,
    time,
    detail: options.detail ?? {},
  };
}
